// Built-in instrument synthesis (§4.11). When no recorded sprite ships in
// /sounds, each sound id is rendered at load time into the same sprite format
// (one buffer + `{ midi: [offset, dur] }` map, sampled every 3 semitones).
//
// Families:
//  - pluck   extended Karplus–Strong (fractional-delay tuning, pick position,
//            loss filter, body EQ, pickup comb, optional amp distortion)
//  - piano   inharmonic additive partials (two detuned strings per partial,
//            two-stage decay) + hammer noise; epiano = decaying 1:1 FM + tine
//  - organ / bowed / wind / brass / synth / voice: sustained band-limited
//            wavetables with seamless loops (integer periods per loop)
//  - mallet  damped modal partials + strike noise
//  - drums   see drums.ts
//
// Pure Float32Array DSP: runs in Node (tests) and in the browser main thread
// or a worker. Rendering is deterministic (seeded noise).

import {
  Biquad,
  TWO_PI,
  addDampedSine,
  clamp,
  dcBlock,
  fadeIn,
  fadeOut,
  formantGain,
  harmonicTable,
  loopNoise,
  loopPlan,
  midiToHz,
  newPcm,
  noiseGen,
  normalize,
} from './dsp';
import { buildSprite, sampleMidis, type Pcm, type RenderedNote, type SpriteData } from './sprite';
import { DRUM_KEYS, renderDrum } from './drums';

export type SoundKind = 'pluck' | 'piano' | 'organ' | 'bowed' | 'wind' | 'brass' | 'mallet' | 'synth' | 'voice' | 'drums';

export const SYNTH_SAMPLE_RATE = 44100;

/** Peak level of each rendered note. */
const NOTE_PEAK = 0.8;

// ---------------------------------------------------------------------------
// Recipes

type Peaks = readonly (readonly [number, number, number])[];
type AmpFn = (k: number, f: number, f0: number) => number;

interface PluckRecipe {
  type: 'pluck';
  /** Loss-filter coefficient 0…0.5 (higher = darker, faster treble decay). */
  rho: number;
  /** Decay (T60, seconds) of the fundamental at MIDI 40 and MIDI 88. */
  t60: [number, number];
  /** Excitation brightness 0…1. */
  bright: number;
  /** Pluck position as a fraction of the string length. */
  pickPos: number;
  /** Electric pickup position (output comb), fraction of the string. */
  pickup?: number;
  /** Body resonance amount (dB of the low body peaks). */
  body?: number;
  /** Second unison string detuned by this many cents (mandolin courses, harpsichord). */
  course?: number;
  dist?: { drive: number; cab: number };
  /** Final lowpass (Hz). */
  tone?: number;
}

interface PianoRecipe {
  type: 'piano';
  electric?: boolean;
}

interface SustainRecipe {
  type: 'sustain';
  amps: AmpFn;
  /** Table base = f0 / baseDiv (organ sub-octave drawbar needs 2). */
  baseDiv?: number;
  /** Crossfade from this spectrum to `amps` during the attack. */
  attackTable?: { amps: AmpFn; time: number };
  /** Extra detuned oscillators: `beats` whole beats per loop. */
  layers?: readonly { beats: number; gain: number }[];
  attack: number;
  pitchAttack?: { cents: number; time: number };
  vibrato?: { rate: number; depth: number; delay: number; fade: number };
  tremolo?: { rate: number; depth: number };
  noise?: { level: number; hp: number; lp: number };
  chiff?: { level: number; time: number; hp: number; lp: number };
  click?: number;
  loopSec?: number;
}

interface MalletRecipe {
  type: 'mallet';
  /** [frequency ratio, amplitude, T60 factor]. */
  modes: readonly (readonly [number, number, number])[];
  /** Fundamental T60 at middle C, and how fast it shortens with pitch. */
  t60: number;
  slope: number;
  strike: { level: number; lp: number; decay: number };
  tremolo?: { rate: number; depth: number };
}

interface DrumRecipe {
  type: 'drums';
}

type Recipe = PluckRecipe | PianoRecipe | SustainRecipe | MalletRecipe | DrumRecipe;

export interface SoundProfile {
  id: string;
  kind: SoundKind;
  /** Sampled MIDI range. */
  range: [number, number];
  /** Playback gain balancing the families. */
  gain: number;
  /** Release after note-off (seconds to about −26 dB). */
  release: number;
  /** Drum kits: nearest key, no pitch shift. */
  exact?: boolean;
  /** One-shot: the note-off is ignored (drums). */
  oneShot?: boolean;
  /** Minimum ring time after the onset, seconds (mallets ring past short notes). */
  minRing?: number;
}

interface SoundDefInternal extends SoundProfile {
  recipe: Recipe;
}

const pow = Math.pow;

// --- sustained spectra ---
const DRAWBARS: Record<number, number> = { 1: 0.5, 2: 1, 3: 0.5, 4: 0.45, 6: 0.18, 8: 0.25, 10: 0.07, 12: 0.06, 16: 0.1 };

const bowedAmps =
  (scale: number, tilt: number): AmpFn =>
  (k, f) =>
    pow(k, -tilt) * (0.3 + formantGain(f / scale, [[290, 120, 1.0], [480, 220, 0.8], [1050, 500, 0.5], [2700, 1400, 0.9]]));

const formantAmps =
  (tilt: number, peaks: Peaks, floor = 0.15): AmpFn =>
  (k, f) =>
    pow(k, -tilt) * formantGain(f, peaks, floor);

const lowpassed =
  (fn: AmpFn, fc: number): AmpFn =>
  (k, f, f0) =>
    fn(k, f, f0) / (1 + (f / fc) * (f / fc));

const bowed = (scale: number, attack: number): SustainRecipe => ({
  type: 'sustain',
  amps: bowedAmps(scale, 1),
  attackTable: { amps: bowedAmps(scale, 0.7), time: 0.1 },
  attack,
  vibrato: { rate: 5.6, depth: 0.0045, delay: 0.2, fade: 0.3 },
  noise: { level: 0.02, hp: 2500, lp: 9000 },
});

const sax = (scale: number): SustainRecipe => ({
  type: 'sustain',
  amps: formantAmps(0.7, [[800 * scale, 700, 1], [1600 * scale, 900, 1.2], [3000 * scale, 1500, 0.4]], 0.1),
  attackTable: { amps: lowpassed(formantAmps(0.7, [[800 * scale, 700, 1]], 0.1), 1200 * scale), time: 0.05 },
  attack: 0.045,
  vibrato: { rate: 5, depth: 0.004, delay: 0.25, fade: 0.3 },
  noise: { level: 0.03, hp: 1500, lp: 7000 },
});

const brass = (peaks: Peaks, tilt: number, attack: number, darkFc: number, vib = 0.002): SustainRecipe => ({
  type: 'sustain',
  amps: formantAmps(tilt, peaks, 0.08),
  attackTable: { amps: lowpassed(formantAmps(tilt, peaks, 0.08), darkFc), time: 0.09 },
  attack,
  pitchAttack: { cents: -30, time: 0.05 },
  vibrato: { rate: 5, depth: vib, delay: 0.3, fade: 0.3 },
  noise: { level: 0.008, hp: 1000, lp: 5000 },
});

const pluck = (r: Omit<PluckRecipe, 'type'>): PluckRecipe => ({ type: 'pluck', ...r });

const mallet = (r: Omit<MalletRecipe, 'type'>): MalletRecipe => ({ type: 'mallet', ...r });

function def(
  id: string,
  kind: SoundKind,
  range: [number, number],
  recipe: Recipe,
  extra: Partial<Pick<SoundProfile, 'gain' | 'release' | 'minRing'>> = {},
): SoundDefInternal {
  const sustained = kind === 'organ' || kind === 'bowed' || kind === 'wind' || kind === 'brass' || kind === 'synth' || kind === 'voice';
  return {
    id,
    kind,
    range,
    recipe,
    gain: extra.gain ?? (sustained ? 0.6 : 1),
    release: extra.release ?? 0.08,
    minRing: extra.minRing,
    exact: kind === 'drums' || undefined,
    oneShot: kind === 'drums' || undefined,
  };
}

const DEFS: SoundDefInternal[] = [
  // ---- plucked ----
  def('gtr_nylon', 'pluck', [33, 100], pluck({ rho: 0.42, t60: [4.5, 1.6], bright: 0.35, pickPos: 0.18, body: 5 })),
  def('gtr_steel', 'pluck', [33, 100], pluck({ rho: 0.28, t60: [6, 2], bright: 0.75, pickPos: 0.12, body: 4 })),
  def('gtr_clean', 'pluck', [33, 100], pluck({ rho: 0.3, t60: [7, 2.5], bright: 0.6, pickPos: 0.13, pickup: 0.18, tone: 6000 })),
  def('gtr_jazz', 'pluck', [33, 100], pluck({ rho: 0.46, t60: [5, 2], bright: 0.3, pickPos: 0.2, pickup: 0.3, tone: 2600 })),
  def('gtr_dist', 'pluck', [33, 100], pluck({ rho: 0.25, t60: [10, 4], bright: 0.7, pickPos: 0.12, pickup: 0.15, dist: { drive: 14, cab: 3800 } }), {
    gain: 0.55,
  }),
  def('bass_finger', 'pluck', [21, 72], pluck({ rho: 0.45, t60: [5, 2.5], bright: 0.3, pickPos: 0.25, pickup: 0.15, tone: 3000 }), { gain: 1.1 }),
  def('bass_pick', 'pluck', [21, 72], pluck({ rho: 0.3, t60: [5, 2.5], bright: 0.7, pickPos: 0.1, pickup: 0.12, tone: 5000 }), { gain: 1.05 }),
  def('bass_acoustic', 'pluck', [21, 72], pluck({ rho: 0.5, t60: [2.5, 1.2], bright: 0.25, pickPos: 0.25, body: 6, tone: 2500 }), { gain: 1.1 }),
  def('banjo', 'pluck', [45, 96], pluck({ rho: 0.15, t60: [1.6, 0.8], bright: 0.9, pickPos: 0.08, body: 3 })),
  def('mandolin', 'pluck', [52, 100], pluck({ rho: 0.25, t60: [2.2, 0.9], bright: 0.8, pickPos: 0.1, course: 3, body: 3 })),
  def('ukulele', 'pluck', [57, 96], pluck({ rho: 0.42, t60: [2.4, 1.0], bright: 0.4, pickPos: 0.2, body: 5 })),
  def('harp', 'pluck', [24, 103], pluck({ rho: 0.38, t60: [6, 1.6], bright: 0.35, pickPos: 0.35, body: 2 }), { release: 0.15 }),
  def('harpsichord', 'pluck', [29, 89], pluck({ rho: 0.1, t60: [5, 1.8], bright: 1, pickPos: 0.06, course: 1.5 })),
  // ---- keyboards ----
  def('piano', 'piano', [21, 108], { type: 'piano' }, { release: 0.1 }),
  def('epiano', 'piano', [28, 103], { type: 'piano', electric: true }, { release: 0.1 }),
  def('organ', 'organ', [24, 96], { type: 'sustain', amps: (k) => DRAWBARS[k] ?? 0, baseDiv: 2, attack: 0.006, click: 0.12, tremolo: { rate: 6.4, depth: 0.08 } }, { gain: 0.45, release: 0.05 }),
  def('accordion', 'organ', [41, 93], {
    type: 'sustain',
    amps: (k, f) => pow(k, -0.8) * (0.6 + formantGain(f, [[1200, 1600, 1]])),
    // Three-reed musette: centre reed plus one beating above and below.
    layers: [
      { beats: 1, gain: 0.5 },
      { beats: -1, gain: 0.5 },
    ],
    attack: 0.04,
    noise: { level: 0.015, hp: 1500, lp: 6000 },
  }, { gain: 0.5 }),
  def('harmonica', 'organ', [60, 96], {
    type: 'sustain',
    amps: (k, f) => (k % 2 ? 1 : 0.55) * pow(k, -0.7) * (0.5 + formantGain(f, [[1700, 1400, 1]])),
    attack: 0.035,
    vibrato: { rate: 5, depth: 0.002, delay: 0.3, fade: 0.3 },
    noise: { level: 0.04, hp: 1200, lp: 7000 },
  }, { gain: 0.5 }),
  def('bagpipe', 'organ', [55, 81], {
    type: 'sustain',
    amps: (k, f) => pow(k, -0.3) * (0.3 + formantGain(f, [[1900, 1300, 1], [3500, 1500, 0.5]])),
    attack: 0.02,
    noise: { level: 0.01, hp: 2000, lp: 8000 },
  }, { gain: 0.45 }),
  // ---- bowed strings ----
  def('violin', 'bowed', [55, 103], bowed(1, 0.09)),
  def('viola', 'bowed', [48, 91], bowed(0.8, 0.1)),
  def('cello', 'bowed', [36, 81], bowed(0.5, 0.12)),
  def('contrabass', 'bowed', [24, 67], bowed(0.35, 0.15)),
  // ---- winds ----
  def('flute', 'wind', [59, 98], {
    type: 'sustain',
    amps: (k) => [1, 0.35, 0.12, 0.06, 0.03, 0.015][k - 1] ?? 0,
    attack: 0.07,
    vibrato: { rate: 5, depth: 0.003, delay: 0.2, fade: 0.3 },
    noise: { level: 0.07, hp: 800, lp: 6000 },
    chiff: { level: 0.15, time: 0.06, hp: 1500, lp: 8000 },
  }),
  def('recorder', 'wind', [60, 98], {
    type: 'sustain',
    amps: (k) => [1, 0.12, 0.06, 0.02][k - 1] ?? 0,
    attack: 0.03,
    noise: { level: 0.025, hp: 1000, lp: 6000 },
    chiff: { level: 0.25, time: 0.04, hp: 2000, lp: 9000 },
  }),
  def('clarinet', 'wind', [50, 94], {
    type: 'sustain',
    amps: (k, f) => (k % 2 ? pow(k, -0.7) : 0.08 / k) / (1 + pow(f / 3000, 2)),
    attackTable: { amps: (k, f) => (k % 2 ? pow(k, -1.2) : 0.04 / k) / (1 + pow(f / 1500, 2)), time: 0.05 },
    attack: 0.05,
    noise: { level: 0.015, hp: 1500, lp: 6000 },
  }),
  def('oboe', 'wind', [58, 93], {
    type: 'sustain',
    amps: formantAmps(0.5, [[1100, 500, 1.5], [2900, 900, 0.6]], 0.15),
    attack: 0.04,
    vibrato: { rate: 5.2, depth: 0.003, delay: 0.25, fade: 0.3 },
    noise: { level: 0.01, hp: 1500, lp: 6000 },
  }),
  def('bassoon', 'wind', [34, 77], {
    type: 'sustain',
    amps: formantAmps(0.6, [[500, 300, 1.5], [1150, 400, 0.7]], 0.15),
    attack: 0.05,
    vibrato: { rate: 5, depth: 0.0025, delay: 0.3, fade: 0.3 },
    noise: { level: 0.01, hp: 1000, lp: 5000 },
  }),
  def('sax_alto', 'wind', [49, 81], sax(1)),
  def('sax_tenor', 'wind', [44, 76], sax(0.8)),
  def('sax_bari', 'wind', [36, 69], sax(0.6)),
  // ---- brass ----
  def('trumpet', 'brass', [52, 84], brass([[1300, 1500, 1], [2700, 1500, 0.5]], 0.2, 0.035, 900)),
  def('trombone', 'brass', [34, 77], brass([[650, 700, 1], [1400, 900, 0.5]], 0.4, 0.05, 500)),
  def('horn', 'brass', [34, 77], brass([[400, 400, 1], [900, 500, 0.3]], 1.0, 0.06, 350, 0.0015)),
  def('tuba', 'brass', [26, 65], brass([[250, 250, 1], [600, 400, 0.3]], 1.2, 0.07, 220, 0.0015)),
  // ---- mallets ----
  def('marimba', 'mallet', [45, 96], mallet({ modes: [[1, 1, 1], [3.93, 0.25, 0.25], [9.2, 0.06, 0.1]], t60: 1.0, slope: 0.5, strike: { level: 0.1, lp: 3000, decay: 0.004 } }), {
    minRing: 0.4,
  }),
  def('vibraphone', 'mallet', [53, 89], mallet({ modes: [[1, 1, 1], [4.0, 0.2, 0.3], [10.1, 0.03, 0.1]], t60: 3.5, slope: 0.3, strike: { level: 0.06, lp: 5000, decay: 0.003 }, tremolo: { rate: 5.5, depth: 0.35 } }), {
    minRing: 0.8,
    release: 0.2,
  }),
  def('glockenspiel', 'mallet', [79, 108], mallet({ modes: [[1, 1, 1], [2.71, 0.4, 0.6], [5.15, 0.2, 0.4], [8.43, 0.08, 0.3]], t60: 3, slope: 0.2, strike: { level: 0.15, lp: 8000, decay: 0.002 } }), {
    minRing: 1,
    release: 0.3,
  }),
  def('steel_drum', 'mallet', [52, 84], mallet({ modes: [[1, 1, 1], [2, 0.55, 0.8], [3, 0.25, 0.6], [4.07, 0.08, 0.4], [1.5, 0.05, 0.5]], t60: 1.4, slope: 0.3, strike: { level: 0.08, lp: 4000, decay: 0.003 } }), {
    minRing: 0.5,
  }),
  def('timpani', 'mallet', [38, 60], mallet({ modes: [[1, 1, 1], [1.5, 0.5, 0.8], [1.98, 0.3, 0.7], [2.44, 0.15, 0.6], [2.95, 0.08, 0.5]], t60: 2.5, slope: 0.3, strike: { level: 0.35, lp: 600, decay: 0.012 } }), {
    minRing: 0.8,
    release: 0.25,
  }),
  // ---- synth / voice ----
  def('synth_lead', 'synth', [24, 108], {
    type: 'sustain',
    amps: (k, f) => 1 / k / (1 + pow(f / 2800, 2)),
    attackTable: { amps: (k) => pow(k, -0.85), time: 0.25 },
    layers: [
      { beats: 2, gain: 0.5 },
      { beats: -2, gain: 0.5 },
    ],
    attack: 0.008,
    vibrato: { rate: 5.5, depth: 0.003, delay: 0.35, fade: 0.3 },
  }, { gain: 0.45 }),
  def('choir', 'voice', [36, 84], {
    type: 'sustain',
    amps: (k, f) => pow(k, -1.1) * formantGain(f, [[750, 130, 1], [1150, 150, 0.6], [2600, 250, 0.25], [3300, 300, 0.15]], 0.02),
    layers: [
      { beats: 1, gain: 0.7 },
      { beats: -1, gain: 0.7 },
    ],
    attack: 0.15,
    vibrato: { rate: 5, depth: 0.006, delay: 0.2, fade: 0.3 },
    noise: { level: 0.02, hp: 1000, lp: 4000 },
  }),
  // ---- drums ----
  def('drums', 'drums', [35, 59], { type: 'drums' }, { gain: 1 }),
];

const BY_ID = new Map(DEFS.map((d) => [d.id, d]));

/** Fallback sound for each family (unknown ids). */
const KIND_FALLBACK: Record<SoundKind, string> = {
  pluck: 'gtr_steel',
  piano: 'piano',
  organ: 'organ',
  bowed: 'violin',
  wind: 'flute',
  brass: 'trumpet',
  mallet: 'marimba',
  synth: 'synth_lead',
  voice: 'choir',
  drums: 'drums',
};

function defFor(id: string, kindHint?: SoundKind): SoundDefInternal {
  const d = BY_ID.get(id);
  if (d) return d;
  const fb = BY_ID.get(KIND_FALLBACK[kindHint ?? 'pluck'])!;
  return { ...fb, id };
}

/** Synthesized sound ids. */
export const SYNTH_SOUND_IDS: readonly string[] = DEFS.map((d) => d.id);

/** Playback profile of a sound id (family, range, gain, release). */
export function soundProfile(id: string, kindHint?: SoundKind): SoundProfile {
  const { recipe: _recipe, ...p } = defFor(id, kindHint);
  void _recipe;
  return p;
}

/** MIDI keys rendered for a sound. */
export function soundKeys(id: string, kindHint?: SoundKind): number[] {
  const d = defFor(id, kindHint);
  if (d.recipe.type === 'drums') return [...DRUM_KEYS];
  return sampleMidis(d.range[0], d.range[1]);
}

function hashSeed(s: string, n: number): number {
  let h = 2166136261 ^ n;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Render one sampled note of a sound. */
export function renderNote(id: string, midi: number, sr = SYNTH_SAMPLE_RATE, kindHint?: SoundKind): RenderedNote {
  const d = defFor(id, kindHint);
  const seed = hashSeed(d.id, midi);
  const r = d.recipe;
  switch (r.type) {
    case 'pluck':
      return { key: midi, data: renderPluck(r, midi, sr, seed) };
    case 'piano':
      return { key: midi, data: r.electric ? renderEPiano(midi, sr) : renderPiano(midi, sr, seed) };
    case 'sustain':
      return renderSustain(r, midi, sr, seed);
    case 'mallet':
      return { key: midi, data: renderMallet(r, midi, sr, seed) };
    case 'drums':
      return { key: midi, data: renderDrum(midi, sr, seed) };
  }
}

/** Render a whole sprite synchronously. */
export function renderSound(id: string, sr = SYNTH_SAMPLE_RATE, kindHint?: SoundKind): SpriteData {
  const notes = soundKeys(id, kindHint).map((m) => renderNote(id, m, sr, kindHint));
  return buildSprite(notes, sr, defFor(id, kindHint).kind === 'drums');
}

/**
 * Render a sprite, yielding to the event loop between notes so a long render
 * never blocks the UI for more than one note.
 */
export async function renderSoundAsync(id: string, sr = SYNTH_SAMPLE_RATE, kindHint?: SoundKind, yieldFn?: () => Promise<void>): Promise<SpriteData> {
  const y = yieldFn ?? (() => new Promise<void>((res) => setTimeout(res, 0)));
  const notes: RenderedNote[] = [];
  let t0 = Date.now();
  for (const m of soundKeys(id, kindHint)) {
    notes.push(renderNote(id, m, sr, kindHint));
    if (Date.now() - t0 > 12) {
      await y();
      t0 = Date.now();
    }
  }
  return buildSprite(notes, sr, defFor(id, kindHint).kind === 'drums');
}

// ---------------------------------------------------------------------------
// Plucked strings: extended Karplus–Strong

function pluckT60(r: PluckRecipe, midi: number): number {
  const [lo, hi] = r.t60;
  const x = clamp((midi - 40) / 48, -0.5, 1.4);
  return lo * pow(hi / lo, x);
}

function decayDur(t60: number, max = 2.5): number {
  return clamp(t60 * 0.8 + 0.25, 0.5, max);
}

/** One Karplus–Strong string added into `out`. */
function ksString(out: Float32Array, sr: number, f0: number, r: PluckRecipe, t60: number, rnd: () => number, gain: number): void {
  const N = sr / f0;
  const rho = r.rho;
  const loopDelay = N - rho;
  const nInt = Math.max(2, Math.floor(loopDelay - 0.1));
  const frac = loopDelay - nInt;
  const C = (1 - frac) / (1 + frac);
  const g = pow(10, -3 / (f0 * t60));

  // Excitation: low-passed noise burst with a pick-position comb.
  const line = new Float32Array(nInt);
  const c = 0.08 + 0.92 * r.bright * r.bright;
  let lp = 0;
  const exc = new Float32Array(nInt);
  for (let i = 0; i < nInt; i++) {
    lp += c * (rnd() - lp);
    exc[i] = lp;
  }
  const M = Math.max(1, Math.round(r.pickPos * N));
  let mean = 0;
  for (let i = 0; i < nInt; i++) {
    line[i] = exc[i] - (i >= M ? exc[i - M] : 0);
    mean += line[i];
  }
  mean /= nInt;
  let pk = 0;
  for (let i = 0; i < nInt; i++) {
    line[i] -= mean;
    pk = Math.max(pk, Math.abs(line[i]));
  }
  if (pk > 0) for (let i = 0; i < nInt; i++) line[i] /= pk;

  let idx = 0;
  let xPrev = 0;
  let apX = 0;
  let apY = 0;
  const len = out.length;
  for (let n = 0; n < len; n++) {
    const x = line[idx];
    out[n] += x * gain;
    const lf = g * ((1 - rho) * x + rho * xPrev);
    xPrev = x;
    const ap = C * lf + apX - C * apY;
    apX = lf;
    apY = ap;
    line[idx] = ap;
    if (++idx === nInt) idx = 0;
  }
}

function renderPluck(r: PluckRecipe, midi: number, sr: number, seed: number): Pcm {
  const f0 = midiToHz(midi);
  const t60 = pluckT60(r, midi);
  const dur = decayDur(r.dist ? Math.min(t60, 3) : t60);
  const out = newPcm(dur * sr);
  const rnd = noiseGen(seed);
  if (r.course) {
    ksString(out, sr, f0 * pow(2, -r.course / 2400), r, t60, rnd, 0.55);
    ksString(out, sr, f0 * pow(2, r.course / 2400), r, t60, rnd, 0.45);
  } else {
    ksString(out, sr, f0, r, t60, rnd, 1);
  }
  if (r.pickup) {
    // Pickup position comb (nasal electric tone).
    const D = Math.max(1, Math.round(r.pickup * (sr / f0)));
    for (let n = out.length - 1; n >= D; n--) out[n] = out[n] - 0.7 * out[n - D];
  }
  if (r.body) {
    Biquad.peaking(sr, 105, 2, r.body).run(out);
    Biquad.peaking(sr, 230, 3, r.body * 0.7).run(out);
    Biquad.peaking(sr, 2800, 1, r.body * 0.4).run(out);
  }
  if (r.dist) {
    Biquad.highpass(sr, 90).run(out);
    normalize(out, 1);
    const drive = r.dist.drive;
    const norm = Math.tanh(drive);
    for (let i = 0; i < out.length; i++) out[i] = Math.tanh(drive * out[i]) / norm;
    const cab1 = Biquad.lowpass(sr, r.dist.cab, 0.8);
    const cab2 = Biquad.lowpass(sr, r.dist.cab * 1.2, 0.6);
    const mid = Biquad.peaking(sr, 1600, 1, 3);
    for (let i = 0; i < out.length; i++) out[i] = mid.process(cab2.process(cab1.process(out[i])));
  }
  if (r.tone) Biquad.lowpass(sr, Math.max(r.tone, f0 * 3), 0.7).run(out);
  dcBlock(out, sr, 25);
  normalize(out, NOTE_PEAK);
  fadeIn(out, 8);
  fadeOut(out, Math.round(out.length * 0.3));
  return out;
}

// ---------------------------------------------------------------------------
// Pianos

function renderPiano(midi: number, sr: number, seed: number): Pcm {
  const f0 = midiToHz(midi);
  const dur = midi < 84 ? 2.5 : clamp(2.5 - (midi - 84) * 0.06, 1, 2.5);
  const out = newPcm(dur * sr);
  const rnd = noiseGen(seed);
  const t60f = clamp(16 * pow(f0 / 27.5, -0.55), 0.8, 16);
  const B = 0.00012 * Math.exp((midi - 21) * 0.037);
  const strike = 1 / 7.3;
  const maxF = Math.min(sr * 0.45, 9000);
  const maxK = midi < 40 ? 32 : midi < 60 ? 24 : midi < 80 ? 16 : 10;
  const detune = (0.4 + 0.5 * ((midi * 7) % 5) / 5) / 1200; // cents → ratio exponent
  const partialAmp = (k: number, fk: number) => (pow(k, -1.05) * (0.15 + Math.abs(Math.sin(Math.PI * k * strike)))) / (1 + pow(fk / 3500, 2));
  // Inharmonicity stretches the upper partials sharp; retune half-way towards
  // the amplitude-weighted stretch of the lowest partials (which dominate the
  // perceived pitch), like a lightly stretch-tuned piano.
  let wSum = 0;
  let sSum = 0;
  for (let k = 1; k <= Math.min(5, maxK); k++) {
    const st = Math.sqrt(1 + B * k * k);
    if (k * f0 * st > maxF) break;
    const a = partialAmp(k, k * f0 * st);
    wSum += a;
    sSum += a * st;
  }
  const fTuned = wSum > 0 ? f0 / Math.sqrt(sSum / wSum) : f0;
  for (let k = 1; k <= maxK; k++) {
    const fk = k * fTuned * Math.sqrt(1 + B * k * k);
    if (fk > maxF) break;
    const a = partialAmp(k, fk);
    if (a < 0.002) continue;
    const t60k = t60f / (1 + fk / 1300);
    const ph = rnd() * Math.PI;
    // Two unison strings: a prompt (fast) and an aftersound (slow) decay.
    addDampedSine(out, sr, fk * pow(2, detune), a * 0.55, t60k * 0.3, ph);
    addDampedSine(out, sr, fk * pow(2, -detune), a * 0.45, t60k, ph + 0.4);
  }
  // Hammer: a short low-passed noise burst.
  const hLen = Math.round(0.02 * sr);
  const hl = Biquad.lowpass(sr, Math.min(8000, 4 * f0 + 1500), 0.7);
  let pk = 0;
  for (let i = 0; i < Math.min(out.length, 2000); i++) pk = Math.max(pk, Math.abs(out[i]));
  const hAmp = 0.12 * (pk || 1);
  for (let i = 0; i < hLen; i++) out[i] += hl.process(rnd()) * hAmp * Math.exp(-i / (0.003 * sr));
  normalize(out, NOTE_PEAK);
  fadeIn(out, 24);
  fadeOut(out, Math.round(0.4 * sr));
  return out;
}

function renderEPiano(midi: number, sr: number): Pcm {
  const f0 = midiToHz(midi);
  const dur = midi < 80 ? 2.5 : clamp(2.5 - (midi - 80) * 0.06, 1, 2.5);
  const out = newPcm(dur * sr);
  const tau = clamp(2.4 * pow(f0 / 261.6, -0.5), 0.4, 4);
  const w = (TWO_PI * f0) / sr;
  const tineF = f0 * 7.1;
  const wt = (TWO_PI * tineF) / sr;
  const useTine = tineF < sr * 0.45;
  const kI = Math.exp(-1 / (0.22 * sr));
  const kA = Math.exp(-1 / (tau * sr));
  const kT = Math.exp(-1 / (0.03 * sr));
  let index = 1.6;
  let amp = 1;
  let tine = 0.25;
  for (let n = 0; n < out.length; n++) {
    const p = w * n;
    let s = Math.sin(p + (index + 0.12) * Math.sin(p));
    if (useTine) s += tine * Math.sin(wt * n);
    out[n] = s * amp;
    index *= kI;
    amp *= kA;
    tine *= kT;
  }
  // Gentle tremolo-free bark: second harmonic emphasis via a peak.
  Biquad.peaking(sr, Math.min(2 * f0, 8000), 1.5, 3).run(out);
  normalize(out, NOTE_PEAK);
  fadeIn(out, 40);
  fadeOut(out, Math.round(0.4 * sr));
  return out;
}

// ---------------------------------------------------------------------------
// Sustained (looped) instruments

const TABLE_SIZE = 2048;

function renderSustain(r: SustainRecipe, midi: number, sr: number, seed: number): RenderedNote {
  const f0 = midiToHz(midi);
  const div = r.baseDiv ?? 1;
  const vib = r.vibrato;
  const trem = r.tremolo;
  const plan = loopPlan(f0 / div, sr, r.loopSec ?? 1, [vib?.rate ?? 0, trem?.rate ?? 0]);
  const L = plan.length;
  const P = plan.periods;
  const fb = plan.freq;
  const vibRate = plan.rates[0];
  const tremRate = plan.rates[1];
  const maxBeats = Math.max(0, ...(r.layers ?? []).map((l) => Math.abs(l.beats)));
  const maxMod = (1 + (vib?.depth ?? 0)) * (1 + maxBeats / P);
  const K = Math.max(1, Math.min(96, Math.floor((sr * 0.45) / (fb * maxMod))));
  const amps = new Float32Array(K);
  for (let k = 1; k <= K; k++) amps[k - 1] = Math.max(0, r.amps(k, k * fb, f0));
  const table = harmonicTable(amps, TABLE_SIZE);
  let attackTable: Float32Array | null = null;
  if (r.attackTable) {
    const a2 = new Float32Array(K);
    for (let k = 1; k <= K; k++) a2[k - 1] = Math.max(0, r.attackTable.amps(k, k * fb, f0));
    attackTable = harmonicTable(a2, TABLE_SIZE);
  }
  const settle =
    Math.max(
      r.attack,
      r.chiff?.time ?? 0,
      r.pitchAttack?.time ?? 0,
      r.attackTable?.time ?? 0,
      vib ? vib.delay + vib.fade : 0,
      r.click ? 0.01 : 0,
    ) + 0.02;
  const loopStart = Math.ceil(settle * sr);
  const loopEnd = loopStart + L;
  const total = loopEnd + 4;
  const out = newPcm(total);

  const noise = r.noise ? loopNoise(L, sr, r.noise.hp, r.noise.lp, seed ^ 0x5bd1e995) : null;
  const rnd = noiseGen(seed);
  let chiff: Float32Array | null = null;
  if (r.chiff) {
    const len = Math.ceil(r.chiff.time * sr);
    chiff = new Float32Array(len);
    const h = Biquad.highpass(sr, r.chiff.hp);
    const l = Biquad.lowpass(sr, r.chiff.lp);
    for (let i = 0; i < len; i++) {
      const e = 1 - i / len;
      chiff[i] = l.process(h.process(rnd())) * e * e * r.chiff.level * 3;
    }
  }
  let click: Float32Array | null = null;
  if (r.click) {
    const len = Math.ceil(0.006 * sr);
    click = new Float32Array(len);
    const l = Biquad.lowpass(sr, 4000);
    for (let i = 0; i < len; i++) click[i] = l.process(rnd()) * r.click * 4 * (1 - i / len);
  }

  const inc0 = (P * TABLE_SIZE) / L;
  const layers = (r.layers ?? []).map((l) => ({ inc: ((P + l.beats) * TABLE_SIZE) / L, gain: l.gain, ph: (rnd() * 0.5 + 0.5) * TABLE_SIZE }));
  let ph = 0;
  const lerp = (t: Float32Array, p: number): number => {
    const i = p | 0;
    const f = p - i;
    return t[i] + (t[i + 1] - t[i]) * f;
  };
  const vibW = (TWO_PI * vibRate) / sr;
  const tremW = (TWO_PI * tremRate) / sr;
  const atkN = Math.max(1, r.attack * sr);
  const pa = r.pitchAttack;
  const paN = pa ? pa.time * sr : 0;
  const atN = r.attackTable ? r.attackTable.time * sr : 0;
  const layerNorm = 1 / (1 + layers.reduce((s, l) => s + l.gain, 0));
  for (let n = 0; n < total; n++) {
    let fm = 1;
    if (vib) {
      const t = n / sr;
      const de = t < vib.delay ? 0 : t < vib.delay + vib.fade ? (t - vib.delay) / vib.fade : 1;
      if (de > 0) fm *= 1 + vib.depth * de * Math.sin(vibW * n);
    }
    if (pa && n < paN) {
      const x = 1 - n / paN;
      fm *= pow(2, (pa.cents * x * x) / 1200);
    }
    ph += inc0 * fm;
    if (ph >= TABLE_SIZE) ph -= TABLE_SIZE;
    let s = lerp(table, ph);
    if (attackTable && n < atN) {
      const x = n / atN;
      const xs = x * x * (3 - 2 * x);
      s = s * xs + lerp(attackTable, ph) * (1 - xs);
    }
    if (layers.length) {
      for (const l of layers) {
        l.ph += l.inc * fm;
        if (l.ph >= TABLE_SIZE) l.ph -= TABLE_SIZE;
        s += l.gain * lerp(table, l.ph);
      }
      s *= layerNorm * 1.6;
    }
    let env = n < atkN ? n / atkN : 1;
    if (trem) env *= 1 - trem.depth * 0.5 * (1 - Math.cos(tremW * n));
    let v = s * env;
    if (noise) v += r.noise!.level * env * noise[n % L] * 3;
    if (chiff && n < chiff.length) v += chiff[n];
    if (click && n < click.length) v += click[n];
    out[n] = v;
  }
  normalize(out, NOTE_PEAK);
  return { key: midi, data: out, loopStart, loopEnd };
}

// ---------------------------------------------------------------------------
// Mallets

function renderMallet(r: MalletRecipe, midi: number, sr: number, seed: number): Pcm {
  const f0 = midiToHz(midi);
  const T = r.t60 * pow(f0 / 261.6, -r.slope);
  const dur = clamp(T * 1.1 + 0.2, 0.4, 2.5);
  const out = newPcm(dur * sr);
  const rnd = noiseGen(seed);
  for (const [ratio, amp, tf] of r.modes) addDampedSine(out, sr, f0 * ratio, amp, T * tf, 0);
  const sLen = Math.round(Math.min(0.05, r.strike.decay * 8) * sr);
  const lp = Biquad.lowpass(sr, r.strike.lp, 0.7);
  for (let i = 0; i < sLen; i++) out[i] += lp.process(rnd()) * r.strike.level * 4 * Math.exp(-i / (r.strike.decay * sr));
  if (r.tremolo) {
    const w = (TWO_PI * r.tremolo.rate) / sr;
    for (let i = 0; i < out.length; i++) out[i] *= 1 - r.tremolo.depth * 0.5 * (1 - Math.cos(w * i));
  }
  dcBlock(out, sr, 20);
  normalize(out, NOTE_PEAK);
  fadeIn(out, 4);
  fadeOut(out, Math.round(out.length * 0.25));
  return out;
}
